import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Grid,
  CircularProgress,
  Divider,
  Alert,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Button
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  Category as CategoryIcon,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { getCategoryById } from '../../services/categoryService';
import { Category } from '../../utils/types';
import { ProductList } from '../../components/store';
// استخدام صورة افتراضية من Cloudinary
const placeholderImage = 'https://res.cloudinary.com/demo/image/upload/v1312461204/sample.jpg';

const CategoryDetailsPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCategoryData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!categoryId) {
          console.error('Category ID is missing');
          setError('معرف الفئة غير موجود');
          setLoading(false);
          return;
        }

        console.log('Loading category data for ID:', categoryId);

        try {
          // تحميل بيانات الفئة الرئيسية
          const categoryData = await getCategoryById(categoryId);
          if (!categoryData) {
            console.error('Category not found for ID:', categoryId);
            setError('الفئة غير موجودة');
            setLoading(false);
            return;
          }
          setCategory(categoryData as Category);
          console.log('Category data loaded:', categoryData);
        } catch (catError) {
          console.error('Error loading category data:', catError);
          setError('حدث خطأ أثناء تحميل بيانات الفئة');
          setLoading(false);
          return;
        }

        setLoading(false);
      } catch (err) {
        console.error('Error in loadCategoryData:', err);
        setError('حدث خطأ أثناء تحميل بيانات الفئة');
        setLoading(false);
      }
    };

    loadCategoryData();
  }, [categoryId]);

  const handleSubcategoryClick = (subcategoryId: string) => {
    navigate(`/category/${categoryId}/${subcategoryId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // التحقق من وجود فئات فرعية
  const hasSubcategories = category?.subCategories && category.subCategories.length > 0;

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4, lg: 5 } }}>
      {/* فتات الخبز */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link component={RouterLink} to="/" color="inherit">
          الرئيسية
        </Link>
        <Link component={RouterLink} to="/categories" color="inherit">
          الفئات
        </Link>
        <Typography color="text.primary">
          {category?.name || 'تفاصيل الفئة'}
        </Typography>
      </Breadcrumbs>

      {/* عنوان الصفحة */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <CategoryIcon sx={{ mr: 1 }} />
          {category?.name || 'تفاصيل الفئة'}
        </Typography>
        {category?.description && (
          <Typography variant="body1" color="text.secondary">
            {category.description}
          </Typography>
        )}
        <Divider sx={{ mt: 2 }} />
      </Box>

      {/* عرض الفئات الفرعية إذا كانت موجودة */}
      {hasSubcategories ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 0 }}>
              الفئات الفرعية
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/category/${categoryId}/products`)}
            >
              عرض جميع منتجات الفئة
            </Button>
          </Box>
          <Grid container spacing={3}>
            {category?.subCategories?.map((subcategory) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={subcategory.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea onClick={() => handleSubcategoryClick(subcategory.id)}>
                    <CardMedia
                      component="img"
                      height="140"
                      image={subcategory.imageUrl || placeholderImage}
                      alt={subcategory.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent>
                      <Typography gutterBottom variant="h6" component="div">
                        {subcategory.name}
                      </Typography>
                      {subcategory.description && (
                        <Typography variant="body2" color="text.secondary">
                          {subcategory.description.length > 100
                            ? `${subcategory.description.substring(0, 100)}...`
                            : subcategory.description}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        <>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 3 }}>
            منتجات الفئة
          </Typography>
          <ProductList categoryId={categoryId} searchQuery="" />
        </>
      )}
    </Container>
  );
};

export default CategoryDetailsPage;
